import { Env } from '@env';
import { addDays, subDays, subHours } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import * as Clipboard from 'expo-clipboard';
import { Image } from 'expo-image';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import * as StoreReview from 'expo-store-review';
import { t } from 'i18next';
import * as React from 'react';
import {
  Alert,
  Platform,
  Pressable,
  ScrollView,
  Share,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useReservation } from '@/api/reservations/use-reservation';
import type { TripStep } from '@/components/trip-timeline';
import TripTimeline, { StepType } from '@/components/trip-timeline';
import { isDesktop, openAddressInMaps } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import useBookCarStore from '@/stores/use-book-car';
import useCheckoutStore from '@/stores/use-checkout';
import useSearchStore from '@/stores/use-search-store';
import { Button, Card, Input, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { AppStore } from '@/ui/icons/app-store';
import { Checkmark } from '@/ui/icons/checkmark';
import { Copy } from '@/ui/icons/copy';
import { GooglePlay } from '@/ui/icons/google-play';
import { Loader } from '@/ui/loader';

const hedgehogSrc = require('../../../public/images/hedgehog.png');

export default function CheckoutComplete() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams<{ id: string }>();
  const router = useRouter();

  React.useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Checkout Complete',
    });
  }, []);

  React.useEffect(() => {
    const promptReview = async () => {
      if (await StoreReview.hasAction()) {
        Alert.alert('Enjoying Eon?', '', [
          {
            text: "❤️ Yes, it's great",
            onPress: () => StoreReview.requestReview(),
          },
          {
            text: 'Maybe later',
            onPress: () => console.log('Cancel Pressed'),
          },
        ]);
      }
    };

    const timeoutId = setTimeout(() => {
      promptReview();
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, []);

  const [shareIcon, setShareIcon] = React.useState<React.ReactNode | null>(
    <Icon
      type="naked"
      icon={<Copy />}
      fillColor={theme.colors.icon.color}
      width={20}
      height={20}
    />
  );

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  const { data: customer } = useCustomer();

  React.useEffect(() => {
    useBookCarStore.getState().reset();
    useCheckoutStore.getState().reset();
    useSearchStore.getState().reset();
  }, []);

  const timelineSteps = React.useMemo(() => {
    if (!resData) return [];

    const verifyNow =
      subHours(new Date(resData.startTime), 24).getTime() < Date.now();

    const steps: TripStep[] = [
      {
        type: StepType.COMPLETED,
        title: 'Book Your Trip',
        body: 'Today',
      },
      {
        type: StepType.NEXT,
        title:
          "Confirm your insurance, driver's license and payment information.",
        body: 'Now',
      },
      ...(resData.holdAmount > 0
        ? [
            {
              type: verifyNow ? StepType.NEXT : StepType.UPCOMING,
              title: `Authorize your $${
                resData.holdAmount / 100
              } security deposit`,
              body: verifyNow
                ? 'Now'
                : format(
                    subDays(
                      toZonedTime(
                        resData.startTime,
                        resData.pickupLocation.timezone
                      ),
                      1
                    ),
                    'MMM dd - hh:mm a'
                  ),
            },
          ]
        : []),
      {
        type: StepType.UPCOMING,
        title: 'Check-In',
        body: format(
          subHours(
            toZonedTime(resData.startTime, resData.pickupLocation.timezone),
            1
          ),
          'MMM dd - hh:mm a'
        ),
      },
      {
        type: StepType.UPCOMING,
        title: resData.customPickupLocation ? 'Delivery' : 'Pickup',
        body: format(
          toZonedTime(resData.startTime, resData.pickupLocation.timezone),
          'MMM dd - hh:mm a'
        ),
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customPickupLocation || resData.pickupLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customPickupLocation || resData.pickupLocation.address}
            </Text>
          </Pressable>
        ),
      },
      {
        type: StepType.UPCOMING,
        title: 'Drop off',
        body: format(
          toZonedTime(resData.endTime, resData.returnLocation.timezone),
          'MMM dd - hh:mm a'
        ),
        footer: (
          <Pressable
            onPress={() => {
              openAddressInMaps(
                resData.customReturnLocation || resData.returnLocation.address
              );
            }}
          >
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textDecorationLine: 'underline',
              }}
            >
              {resData.customReturnLocation || resData.returnLocation.address}
            </Text>
          </Pressable>
        ),
      },
      {
        type: StepType.UPCOMING,
        title: 'Get your final receipt',
        body: `By ${format(
          addDays(
            toZonedTime(resData.endTime, resData.returnLocation.timezone),
            2
          ),
          'MMM dd'
        )}`,
      },
    ];

    return steps;
  }, [resData]);

  const handleShare = async () => {
    const link = `${Env.BASE_URL}?ref=${customer?.referralCode}`;

    if (Platform.OS === 'web') {
      const copied = await Clipboard.setStringAsync(link);

      if (!copied) {
        showErrorMessage('Issue copying to clipboard');
      } else {
        Toast.show({
          text1: 'Copied to clipboard',
          type: 'success',
        });
      }
    }

    try {
      await Share.share({
        message: link,
      });
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  if (isFetchingReservation) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {!isDesktop() ? (
        <Stack.Screen
          options={{
            headerTitle: resData
              ? `${format(
                  toZonedTime(
                    resData.startTime,
                    resData.pickupLocation.timezone
                  ),
                  'MM.dd.yyyy'
                )} - ${resData.pickupLocation.city}`
              : undefined,
            headerBackVisible: false,
            headerLeft: () => null,
          }}
        />
      ) : null}

      <View style={styles.scrollViewContainer}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'center', padding: 16 }}
          style={{ width: '100%' }}
        >
          <View style={styles.scrollViewChild}>
            <View style={{ gap: 16 }}>
              <Text
                tx="checkoutComplete.header"
                values={{
                  name:
                    resData.customer.firstName ?? resData.customer.firstname,
                }}
                type="largeTitle"
              />

              <Text
                tx="checkoutComplete.body"
                values={{ email: resData.customer.email }}
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              />
            </View>

            <View style={{ gap: 16 }}>
              <Card>
                <View
                  style={{
                    borderBottomColor: theme.colors.card.default.borderColor,
                    borderBottomWidth: 1,
                  }}
                >
                  <View style={{ padding: 16 }}>
                    <Text tx="checkoutComplete.rentalTimeline" type="title2" />
                  </View>
                </View>

                <View style={{}}>
                  <Image
                    source={hedgehogSrc}
                    style={{
                      width: 80,
                      height: 100,
                      borderRadius: 16,
                      position: 'absolute',
                      top: 0,
                      right: 20,
                      zIndex: 1,
                    }}
                  />

                  <View
                    style={{
                      padding: 16,
                      gap: 16,
                    }}
                  >
                    <TripTimeline steps={timelineSteps} />

                    {isDesktop() ? (
                      <View style={{ alignItems: 'center' }}>
                        <Button
                          tx="checkoutComplete.manageYourBookings"
                          type="primary"
                          onPress={() => {
                            router.navigate('/trips');
                          }}
                          style={{
                            width: '100%',
                          }}
                        />
                      </View>
                    ) : null}
                  </View>
                </View>
              </Card>

              <Card>
                <View style={{ padding: 16, gap: 24 }}>
                  <View style={{ gap: 8 }}>
                    <Text tx="checkoutComplete.referAFriend" type="title2" />

                    <Text
                      tx="checkoutComplete.referAFriendBody"
                      type="bodyRegular"
                      style={{ color: theme.colors.text.secondary }}
                    />
                  </View>

                  <View style={{ gap: 16 }}>
                    <Input
                      label={t('checkoutComplete.referralLink')}
                      value={`${Env.BASE_URL}?ref=${customer?.referralCode}`}
                      rightIcon={shareIcon}
                      onRightIconPress={async () => {
                        try {
                          await Clipboard.setStringAsync(
                            `${Env.BASE_URL}?ref=${customer?.referralCode}`
                          );

                          Toast.show({
                            text1: 'Copied successfully',
                            type: 'success',
                          });

                          setShareIcon(
                            <Icon
                              type="naked"
                              icon={<Checkmark />}
                              stroke={theme.colors.input.success.helperText}
                              fillColor={theme.colors.input.success.helperText}
                              width={14}
                              height={14}
                            />
                          );

                          setTimeout(() => {
                            setShareIcon(
                              <Icon
                                type="naked"
                                icon={<Copy />}
                                fillColor={theme.colors.icon.color}
                                width={20}
                                height={20}
                              />
                            );
                          }, 2000);
                        } catch (e) {
                          showErrorMessage('Issue copying to clipboard');
                        }
                      }}
                      disabled
                    />

                    <Button
                      type="primary"
                      tx="checkoutComplete.share"
                      onPress={handleShare}
                    />
                  </View>
                </View>
              </Card>

              {Platform.OS === 'web' ? (
                <Card>
                  <View style={{ padding: 16 }}>
                    <View style={{ gap: 8 }}>
                      <Text
                        tx="checkoutComplete.manageBookingsFromApp"
                        type="title2"
                      />

                      <Text
                        tx="checkoutComplete.manageBookingsFromAppBody"
                        type="bodyRegular"
                        style={{ color: theme.colors.text.secondary }}
                      />

                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'center',
                        }}
                      >
                        <Pressable
                          onPress={async () => {
                            window.open(
                              'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911',
                              '_blank'
                            );
                          }}
                        >
                          <Icon
                            type="naked"
                            icon={<AppStore />}
                            width={160}
                            height={90}
                          />
                        </Pressable>

                        <Pressable
                          onPress={() => {
                            window.open(
                              'https://play.google.com/store/apps/details?id=com.eon.mobile',
                              '_blank'
                            );
                          }}
                        >
                          <Icon
                            type="naked"
                            icon={<GooglePlay />}
                            width={160}
                            height={90}
                          />
                        </Pressable>
                      </View>
                    </View>
                  </View>
                </Card>
              ) : null}
            </View>
          </View>
        </ScrollView>
      </View>

      <View style={styles.overviewContainer}>
        <View
          style={{
            alignItems: 'center',
            paddingTop: 16,
          }}
        >
          <Button
            tx="checkoutComplete.manageYourBookings"
            type="primary"
            onPress={() => {
              router.navigate('/trips');
            }}
            style={{
              width: '80%',
            }}
          />

          <Button
            tx="checkoutComplete.newSearch"
            type="ghost"
            onPress={() => {
              router.navigate('/');
            }}
            style={{
              width: '50%',
            }}
          />
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  scrollViewContainer: {
    paddingBottom: 0,
    height: {
      xs: '82%',
      md: '100%',
    },
    width: '100%',
  },
  scrollViewChild: {
    width: {
      md: 600,
    },
    gap: 16,
    paddingBottom: 16,
  },
  overviewContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_2,
    display: {
      md: 'none',
    },
  },
}));
